import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { SharedUserApi, type TypePick } from '@reconcept-platform/shared/data-access';
import { type User } from '@reconcept-platform/shared/types';
import { resourceValueSafe } from '@reconcept-platform/shared/util';
import { AvatarComponent } from '../avatar.component';

export type UserAvatar_User = TypePick<User, 'firstName' | 'lastName'>;

@Component({
  selector: 'rcp-user-avatar',
  styleUrls: ['./user-avatar.component.scss'],
  template: `
    <rcp-avatar [class.no-src]="!avatarUrl()" [rounded]="true" [src]="avatarUrl()" [size]="size()" [active]="active()">
      <!-- default to icon with initials if no image -->
      @if (!avatarUrl()) {
        <span class="initials"> {{ displayedInitials() }} </span>
      }
    </rcp-avatar>
  `,
  imports: [AvatarComponent],
})
export class UserAvatarComponent {
  private userApi = inject(SharedUserApi);

  // user thumbnail or avatar will be used by default
  readonly user = input<UserAvatar_User>();

  // alternatively, a direct src url can be used
  readonly src = input<string>();

  // initials can be set directly
  readonly initials = input<string>();

  readonly size = input<'small' | 'medium' | 'large'>('small');
  readonly quality = input<'normal' | 'high'>('normal');

  readonly active = input<boolean>();

  private readonly avatarResource = rxResource({
    params: () => {
      const userId = this.user()?.id;
      return !this.src() && userId ? userId : undefined;
    },
    stream: ({ params: userId }) => this.userApi.watchAvatar(userId),
  });
  private readonly avatar = resourceValueSafe(this.avatarResource);

  readonly displayedInitials = computed(() => {
    const initials = this.initials();
    const user = this.user();

    // use manually set initials
    if (initials) {
      return initials;
    } else if (user) {
      // try to derive initials from name
      return (user.firstName.substring(0, 1) + user.lastName.substring(0, 1)).toUpperCase();
    } else {
      return '';
    }
  });

  // the src actually used
  readonly avatarUrl = computed(() => {
    const src = this.src();
    if (src) {
      return src;
    }

    const user = this.avatar();
    if (!user) {
      return undefined;
    }

    if (user.thumbUrl && user.avatarUrl) {
      return (this.quality() === 'normal' || !user.avatarUrl ? user.thumbUrl : user.avatarUrl) ?? undefined;
    }
    return user.fullSizeAvatarUrl ?? undefined;
  });
}
